/**
 * accounting_export_email_log — one row per (tenant, export period) considered.
 *
 * ClickUp 86bbruq31 / migration 0084.
 *
 * WHY THIS TABLE EXISTS
 * ---------------------
 * The scheduled-export sweeper ticks HOURLY, and several ECS tasks run it
 * concurrently. The Redis SET-NX lock makes a double-send unlikely; the unique
 * index here makes it impossible. Same belt-and-braces posture as
 * `platform_disposal_daily_email_*_record_send`: INSERT first and let the unique
 * violation — not a read-then-write inspection, which races — decide who sends.
 *
 * A duplicate is not cosmetic. It is a second CSV of the same invoices arriving
 * in an office inbox, and Transaction Pro Importer will happily import a
 * RefNumber it has already seen, creating duplicate invoices in the customer's
 * books. That is discovered at reconciliation, not at import.
 *
 * WHY A ROW IS WRITTEN FOR AN EMPTY PERIOD
 * ----------------------------------------
 * A period with no invoices records `invoiceCount: 0` and sends nothing. Writing
 * the row anyway is what stops an empty week being reconsidered every hour for
 * the next week, and it keeps "we looked at this period and it was empty"
 * visible rather than indistinguishable from "the sweeper never ran".
 *
 * WHY LOCAL DATES, NOT TIMESTAMPS
 * -------------------------------
 * `periodStart` / `periodEnd` are calendar dates in the tenant's own timezone
 * (DEC-004/DEC-066), not instants. Storing instants would reintroduce exactly
 * the UTC-rollover ambiguity that `hauler_tenant_settings.timezone` exists to
 * remove.
 */

import {
  date,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uuid,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';

export const accountingExportEmailLog = pgTable(
  'accounting_export_email_log',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** 'WEEKLY' | 'MONTHLY' — constrained by a DB CHECK in migration 0084. */
    frequency: text('frequency').notNull(),
    periodStart: date('period_start').notNull(),
    periodEnd: date('period_end').notNull(),
    invoiceCount: integer('invoice_count').notNull().default(0),
    lineCount: integer('line_count').notNull().default(0),
    recipientCount: integer('recipient_count').notNull().default(0),
    emailsSent: integer('emails_sent').notNull().default(0),
    sendErrors: integer('send_errors').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // The idempotency guarantee. Keyed on the full window rather than periodEnd
    // alone so that changing dayOfWeek — which legitimately shifts the window —
    // is not mistaken for a resend of a period already covered.
    uniqueIndex('accounting_export_email_log_period_uq').on(t.tenantId, t.periodStart, t.periodEnd),
    // Mirrors the hauler_tenant_settings_isolation pattern; migration 0084
    // creates the same-named DB-side policy. Mirroring keeps schema-diff tooling
    // aligned with the real policies (Plan 06-01 / MEDIUM-4).
    pgPolicy('accounting_export_email_log_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type AccountingExportEmailLog = typeof accountingExportEmailLog.$inferSelect;
export type NewAccountingExportEmailLog = typeof accountingExportEmailLog.$inferInsert;
export const accountingExportEmailLogSelectSchema = createSelectSchema(accountingExportEmailLog);
export const accountingExportEmailLogInsertSchema = createInsertSchema(accountingExportEmailLog);
